import axios from 'axios';

const BASE_URL = 'https://api.almoosawi.co';
const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

// Content API — uses static Strapi API token (read-only)
const contentClient = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${STRAPI_TOKEN}`,
  },
});

// Auth API — uses session JWT from login, auto-logout on 401
const authClient = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const attachAuthToken = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

authClient.interceptors.request.use(attachAuthToken, (e) => Promise.reject(e));
authClient.interceptors.response.use((r) => r, (error) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/dash/login';
  }
  return Promise.reject(error);
});

const apiService = {
  // Authentication — Strapi Users & Permissions login
  login: async (email, password) => {
    const response = await authClient.post('/auth/local', { identifier: email, password });
    const { jwt, user } = response.data;
    localStorage.setItem('token', jwt);
    // Fetch role using the static API token which has broader read permissions
    try {
      const roleResponse = await contentClient.get(`/users/${user.id}?populate=role`);
      const fullUser = { ...user, role: roleResponse.data.role };
      localStorage.setItem('user', JSON.stringify(fullUser));
      return { token: jwt, user: fullUser };
    } catch {
      localStorage.setItem('user', JSON.stringify(user));
      return { token: jwt, user };
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  validateToken: async () => {
    const response = await authClient.get('/users/me');
    const user = response.data;
    try {
      const roleResponse = await contentClient.get(`/users/${user.id}?populate=role`);
      const fullUser = { ...user, role: roleResponse.data.role };
      localStorage.setItem('user', JSON.stringify(fullUser));
      return fullUser;
    } catch {
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    }
  },

  // Live collection
  getLive: async () => {
    const response = await contentClient.get('/lives?populate=*');
    const items = response.data?.data || [];
    return Array.isArray(items) ? items[0] : items || null;
  },

  // Categories (types) collection
  getCategories: async ({ search = '' } = {}) => {
    const params = new URLSearchParams({ 'populate': '*', 'sort': 'Name:asc' });
    if (search) params.append('filters[Name][$containsi]', search);
    const response = await contentClient.get(`/types?${params}`);
    return response.data;
  },

  // Readers collection
  getReaders: async ({ search = '' } = {}) => {
    const params = new URLSearchParams({ 'populate': '*', 'sort': 'Name:asc' });
    if (search) params.append('filters[Name][$containsi]', search);
    const response = await contentClient.get(`/readers?${params}`);
    return response.data;
  },

  // Audios (MjalisVO) collection
  getAudios: async ({ page = 1, pageSize = 24, search = '' } = {}) => {
    const params = new URLSearchParams({
      'populate': '*',
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
      'sort': 'createdAt:desc',
    });
    if (search) params.append('filters[Title][$containsi]', search);
    const response = await contentClient.get(`/mjalis-vos?${params}`);
    return response.data;
  },

  // Videos (MjalisYT) collection
  getVideos: async ({ page = 1, pageSize = 24, search = '' } = {}) => {
    const params = new URLSearchParams({
      'populate': '*',
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
      'sort': 'createdAt:desc',
    });
    if (search) params.append('filters[Title][$containsi]', search);
    const response = await contentClient.get(`/mjalis-yts?${params}`);
    return response.data;
  },

  // Generic CRUD operations (kept for other pages)
  getCollection: async (collectionName, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/${collectionName}${queryParams ? `?${queryParams}` : ''}`;
    const response = await contentClient.get(url);
    return response.data;
  },

  getCollectionItem: async (collectionName, id) => {
    const response = await contentClient.get(`/${collectionName}/${id}`);
    return response.data;
  },

  createCollectionItem: async (collectionName, data) => {
    const response = await contentClient.post(`/${collectionName}`, { data });
    return response.data;
  },

  updateCollectionItem: async (collectionName, id, data) => {
    const response = await contentClient.put(`/${collectionName}/${id}`, { data });
    return response.data;
  },

  deleteCollectionItem: async (collectionName, id) => {
    const response = await contentClient.delete(`/${collectionName}/${id}`);
    return response.data;
  },

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('files', file);
    const response = await contentClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getMediaFiles: async () => {
    const response = await contentClient.get('/upload/files');
    return response.data;
  },
};

export default apiService;
